/**
 * Calendar-Helpers — Spans aus dem Server-Lesemodell, Aufenthaltsfilter und Belegungstexte.
 * Datumsrechnung steht in `calendarDates.ts`, die Kanalzuordnung in `calendarChannels.ts`; beide
 * werden hier weitergereicht, weil andere Bereiche sie über diesen Pfad beziehen.
 *
 * Reine Funktionen, keine React-Abhaengigkeit.
 */
import type { CalendarBookingSpan, CalendarPropertyRow } from '@/shared/api';
import { nightsBetween } from './calendarDates';

export * from './calendarChannels';
export * from './calendarDates';

export interface BookingSpan {
  bookingId: string;
  channel: string;
  channelCategory: string;
  guestName: string | null;
  guestCount: number;
  adults: number;
  children: number;
  infants: number;
  occupancyDataQualityCode: string;
  phone: string | null;
  email: string | null;
  bookingReference: string;
  externalReservationCode: string | null;
  totalPrice: number | null;
  priceEvidenceState: 'Known' | 'Pending' | 'Conflict';
  bookingStatus: string;
  checkInDate: string;
  checkOutDate: string;
  /** Tatsächlicher Abreisetag nach vollzogener vorzeitiger Abreise; sonst null. */
  actualDepartureDate: string | null;
  earlyDeparturePlan: CalendarBookingSpan['earlyDeparturePlan'];
  startIndex: number;
  endIndex: number;
  /** Sichtbare Nächte im geladenen Fenster — für die Geometrie des Balkens. */
  nights: number;
  /** Alle Nächte des Aufenthalts bis zur tatsächlichen oder gebuchten Abreise. */
  stayNights: number;
  isClippedAtStart: boolean;
  isClippedAtEnd: boolean;
  isCheckoutOnlyAtRangeStart: boolean;
  hasTransitionOut: boolean;
  hasTransitionIn: boolean;
}

export function buildBookingSpans(spans: CalendarBookingSpan[]): BookingSpan[] {
  return spans.map((span) => {
    const actualDepartureDate = span.actualDepartureDate ?? null;
    return {
      bookingId: span.bookingId,
      channel: span.channelDisplayName,
      channelCategory: span.channelCategory,
      guestName: span.guestName ?? null,
      guestCount: span.guestCount,
      adults: span.adults,
      children: span.children,
      infants: span.infants,
      occupancyDataQualityCode: span.occupancyDataQualityCode,
      phone: span.phone ?? null,
      email: span.email ?? null,
      bookingReference: span.bookingReference,
      externalReservationCode: span.externalReservationCode ?? null,
      totalPrice: span.totalPrice,
      priceEvidenceState: span.priceEvidenceState ?? 'Known',
      bookingStatus: span.bookingStatus,
      checkInDate: span.checkInDate,
      checkOutDate: span.checkOutDate,
      actualDepartureDate,
      earlyDeparturePlan: span.earlyDeparturePlan ?? null,
      startIndex: span.visibleStartIndex,
      endIndex: span.visibleStartIndex + Math.max(0, span.visibleNightCount - 1),
      nights: span.visibleNightCount,
      stayNights: nightsBetween(span.checkInDate, actualDepartureDate ?? span.checkOutDate),
      isClippedAtStart: span.isClippedAtStart,
      isClippedAtEnd: span.isClippedAtEnd,
      isCheckoutOnlyAtRangeStart: span.isCheckoutOnlyAtRangeStart,
      hasTransitionOut: span.hasTransitionOut,
      hasTransitionIn: span.hasTransitionIn,
    };
  });
}

export type StayFilter = 'all' | 'current' | 'future';

export function matchesStayFilter(span: BookingSpan, focus: string, filter: StayFilter): boolean {
  if (filter === 'all') return true;
  const departure = span.actualDepartureDate ?? span.checkOutDate;
  if (filter === 'current') return span.checkInDate <= focus && focus < departure;
  return span.checkInDate > focus;
}

export function shouldRenderBookingPill(
  span: Pick<BookingSpan, 'nights' | 'hasTransitionOut'>,
): boolean {
  return span.nights > 0 || span.hasTransitionOut;
}

export function occupancyLabel(
  span: Pick<
    BookingSpan,
    'guestCount' | 'adults' | 'children' | 'infants' | 'occupancyDataQualityCode'
  >,
): string {
  if (span.occupancyDataQualityCode !== 'Verified') {
    return `${span.guestCount} Gäste · Aufteilung nicht verifiziert`;
  }
  const parts = [`${span.adults} ${span.adults === 1 ? 'Erwachsener' : 'Erwachsene'}`];
  if (span.children) parts.push(`${span.children} ${span.children === 1 ? 'Kind' : 'Kinder'}`);
  if (span.infants) {
    parts.push(`${span.infants} ${span.infants === 1 ? 'Kleinkind' : 'Kleinkinder'}`);
  }
  return parts.join(' · ');
}

export function bookedDayCount(row: CalendarPropertyRow): number {
  return row.cells.filter((c) => c.status === 'booked').length;
}
